import { useState, useEffect } from 'react'
import { useMetaData } from '../queries/useMetaData.js'
import { useUpdateMeta } from '../mutations/useUpdateMeta.js'
import { useGoalsData } from '../queries/useGoalsData.js'
import { useUIStore } from '../store/uiStore.js'
import { goalKey, pillarIndexByName, timeAgo } from '../data/metaKeys.js'

/**
 * WeeklySummary — per-goal three-field weekly update.
 *
 * Fields (matching q2-goals.html lines 1127–1129):
 *   🟣 TLDR   — one sentence: where does this goal stand this week
 *   🟢 Wins   — what went well, milestones hit
 *   🟡 Flags  — what's at risk, blocked, or needs leadership attention
 *
 * Stored under meta.weeklySummary[`p${pi}-g${gi}`] as:
 *   { tldr, wins, flags, savedAt, savedBy }
 *
 * Only shown on top-level goals. Sub-goals use the simpler `update`
 * field on the goal data itself (unchanged from before).
 *
 * Writes are optimistic and debounced via local-draft state (save on blur).
 */
export default function WeeklySummary({ pillarName, goalIndex }) {
  const currentUser = useUIStore((s) => s.currentUser)
  const setEditingGoalKey = useUIStore((s) => s.setEditingGoalKey)
  const clearEditingGoalKey = useUIStore((s) => s.clearEditingGoalKey)
  const { data: goalsData } = useGoalsData()
  const { data: meta } = useMetaData()
  const updateMeta = useUpdateMeta()

  const pi = pillarIndexByName(goalsData, pillarName)
  const key = pi >= 0 ? goalKey(pi, goalIndex) : null
  const saved = (key && meta.weeklySummary?.[key]) || {}

  // Local draft state — so typing doesn't fire a save on every keystroke.
  // Save happens on blur.
  const [draft, setDraft] = useState({
    tldr: saved.tldr || '',
    wins: saved.wins || '',
    flags: saved.flags || '',
  })
  const [editing, setEditing] = useState(null) // 'tldr' | 'wins' | 'flags' | null

  // When the server value changes and we're NOT mid-edit, re-sync the draft.
  useEffect(() => {
    if (editing) return
    setDraft({
      tldr: saved.tldr || '',
      wins: saved.wins || '',
      flags: saved.flags || '',
    })
  }, [saved.tldr, saved.wins, saved.flags, editing])

  // While editing any of the three fields, broadcast that we're editing
  // THIS goal so other clients see a pulsing avatar next to its title.
  useEffect(() => {
    if (!editing || pi < 0) return
    setEditingGoalKey(`${pillarName}:${goalIndex}`)
    return () => clearEditingGoalKey()
  }, [editing, pillarName, goalIndex, pi, setEditingGoalKey, clearEditingGoalKey])

  const saveField = (field) => {
    if (!key) return
    const newValue = draft[field]
    if (newValue === (saved[field] || '')) {
      setEditing(null)
      return
    }
    updateMeta.mutate((m) => ({
      ...m,
      weeklySummary: {
        ...m.weeklySummary,
        [key]: {
          tldr: draft.tldr,
          wins: draft.wins,
          flags: draft.flags,
          [field]: newValue,
          savedAt: Date.now(),
          savedBy: currentUser || 'Unknown',
        },
      },
    }))
    setEditing(null)
  }

  const fields = [
    {
      field: 'tldr',
      label: 'TLDR',
      dot: '🟣',
      color: '#9333ea',
      bg: '#faf5ff',
      border: '#e9d5ff',
      hint: 'Click to add a multi-sentence status summary',
      placeholder:
        "Where this goal stands right now — 2–3 sentences on progress, what changed this week, and the path to target. This is the text that feeds your executive summary, so make it substantive.",
      minH: 72,
    },
    {
      field: 'wins',
      label: 'Wins',
      dot: '🟢',
      color: '#16a34a',
      bg: '#f0fdf4',
      border: '#bbf7d0',
      hint: 'Click to add the week\'s wins',
      placeholder:
        "What went well this week? Milestones hit, deals closed, work shipped, people unblocked. Specifics help the exec summary tell a real story.",
      minH: 64,
    },
    {
      field: 'flags',
      label: 'Flags / Blockers / Risks',
      dot: '🟡',
      color: '#d97706',
      bg: '#fffbeb',
      border: '#fde68a',
      hint: 'Click to flag blockers, risks, or asks',
      placeholder:
        "What's at risk, blocked, or needs leadership attention? Be concrete — who needs to unblock what, and by when.",
      minH: 64,
    },
  ]

  const hasAnyContent = saved.tldr || saved.wins || saved.flags

  return (
    <div
      data-tour="weekly-summary"
      style={{
        background: '#f9fafb',
        border: '1px solid var(--border)',
        borderRadius: 10,
        padding: 12,
        marginTop: 12,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 10,
        }}
      >
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            color: 'var(--muted)',
            textTransform: 'uppercase',
            letterSpacing: 0.08,
          }}
        >
          📋 Weekly Summary
        </div>
        {saved.savedAt && (
          <div style={{ fontSize: 10, color: 'var(--muted-light)' }}>
            {saved.savedBy ? `${saved.savedBy} · ` : ''}{timeAgo(saved.savedAt)}
          </div>
        )}
      </div>

      {fields.map((f) => {
        const isEditing = editing === f.field
        const value = draft[f.field]
        const empty = !value

        return (
          <div
            key={f.field}
            style={{
              marginBottom: 8,
              padding: '8px 10px',
              background: f.bg,
              border: `1px solid ${f.border}`,
              borderRadius: 8,
            }}
          >
            <div
              style={{
                fontSize: 9,
                fontWeight: 800,
                color: f.color,
                textTransform: 'uppercase',
                letterSpacing: 0.4,
                marginBottom: 4,
              }}
            >
              {f.dot} {f.label}
            </div>

            {isEditing ? (
              <textarea
                autoFocus
                value={value}
                onChange={(e) => setDraft((d) => ({ ...d, [f.field]: e.target.value }))}
                onBlur={() => saveField(f.field)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    setDraft((d) => ({ ...d, [f.field]: saved[f.field] || '' }))
                    setEditing(null)
                  }
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                    saveField(f.field)
                  }
                }}
                placeholder={f.placeholder}
                style={{
                  width: '100%',
                  minHeight: f.minH,
                  fontSize: 12,
                  fontFamily: 'inherit',
                  color: 'var(--text-secondary)',
                  border: '1px solid var(--accent)',
                  borderRadius: 6,
                  padding: '6px 8px',
                  background: '#fff',
                  resize: 'vertical',
                  lineHeight: 1.5,
                  outline: 'none',
                }}
              />
            ) : (
              <div
                onClick={() => setEditing(f.field)}
                style={{
                  fontSize: 12,
                  color: empty ? 'var(--muted-light)' : 'var(--text-secondary)',
                  fontStyle: empty ? 'italic' : 'normal',
                  lineHeight: 1.5,
                  cursor: 'pointer',
                  minHeight: empty ? 20 : undefined,
                  padding: '2px 0',
                  whiteSpace: 'pre-wrap',
                }}
                title={empty ? f.placeholder : 'Click to edit'}
              >
                {empty ? f.hint : value}
              </div>
            )}
          </div>
        )
      })}

    </div>
  )
}
